import { usePage } from '@inertiajs/react'
import { Fragment, useEffect } from 'react'

import { Container, Stack, Grid, Group, Title, Text, Box } from '@mantine/core'
import StationMapPanel from '../_StationMapPanel'
import TideChart from './TideChart'
import Insights from '../_Insights'
import AppPromo from '../../_AppPromo'
import Breadcrumb from '../_Breadcrumb'
import ChartsCard from '../_ChartsCard'
import Credits from '../_Credits'
import FavoriteButton from '../_FavoriteButton'
import { locationLine, moonPhaseLabel, moonPhaseEmoji } from '../_format'
import { NowTile, TileStat, CoefPill, TILE } from '../_tiles'
import { WeekCard, WeekSection, WeekRow, ArrowLabel, weekDate } from '../_week'
import { dayConditionGlyph } from '../_weather/ConditionGlyph'
import { useWeather } from '../_weather/useWeather'
import { alignDaily, groupHourlyByLocalDay } from '../_weather/weatherData'
import ConditionsSummary from '../_weather/ConditionsSummary'
import MarineForecastRail from '../_weather/MarineForecastRail'
import DayWeatherBand from '../_weather/DayWeatherBand'
import DayWeatherBandSkeleton from '../_weather/DayWeatherBandSkeleton'
import { useFormat } from '../../_units'
import { useStations } from '../../_stations'
import { useI18n } from '../../_i18n'
import Seo from '../../_Seo'
import { stationCrumbs, stationPlace, breadcrumbList } from '../../_structuredData'
import { useLocalePath } from '../../_localePath'
import { useStationTimezoneMatch } from '../_stationTimezoneMatch'

// Server-rendered tide station page. Receives the exact v2 API payload as
// `tide_station` (Web::StationsController#tide reuses build_tide_response) plus
// a small `seo` prop (parent permalinks). Renders a location breadcrumb, name,
// tide/sun/moon, and the week — backed by matching JSON-LD.

export default function TideStationPage({
  tide_station,
  weather_panel = false,
  layer_coverage: layerCoverage = null,
  marine_conditions = null,
  station_now = null,
  timezone_abbr: timezoneAbbr = null,
  charts = [],
  seo = {},
}) {
  const s = tide_station || {}
  // The head script in layouts/_station_timezone only runs on a hard
  // load/reload; this covers Inertia navigating here client-side — see
  // _stationTimezoneMatch for why both are needed.
  useStationTimezoneMatch(s.timezone)
  const { t, locale } = useI18n()
  const title = t('tide_title', { name: s.name })
  const location = locationLine(s)
  const days = Object.entries(s.next_seven_days || {})
  const { site_url: siteUrl = '', canonical_url: canonicalUrl = '' } = usePage().props

  const fmt = useFormat()
  const localePath = useLocalePath()
  const crumbs = stationCrumbs(s, seo, localePath)

  // Client-fetched from the marine-weather CDN (never server-side — no live API
  // calls during a request). Renders nothing when gated off or the station
  // isn't in the feed's registry.
  const weather = useWeather('tide', s.id, weather_panel)
  // Aligned to the same `days` keys as the tide payload's next_seven_days, so
  // each day's Weather band can sit right beside that day's Tides band.
  const weatherDaily =
    weather.status === 'ready' && weather.data
      ? alignDaily(
          weather.data.daily,
          days.map(([date]) => date),
        )
      : []
  const weatherHourlyByDay =
    weather.status === 'ready' && weather.data
      ? groupHourlyByLocalDay(weather.data.hourly, s.timezone)
      : {}
  // Only when the feed says something is active: a quiet or absent warning feed
  // must never render as an all-clear, here or anywhere else on the page.
  const weatherWarnings =
    weather.status === 'ready' && weather.data?.warnings?.status === 'active'
      ? weather.data.warnings.items || []
      : []

  // Record this tide station as a recent on visit (client-only; name-guarded).
  const { recordRecent } = useStations()
  useEffect(() => {
    if (s.id != null && s.name) recordRecent('tide', { id: s.id, name: s.name })
  }, [s.id])

  return (
    <>
      <Seo
        title={title}
        description={t('tide_seo_description', {
          name: s.name,
          location: location ? ` - ${location}` : '',
        })}
        jsonLd={[stationPlace(canonicalUrl, s), breadcrumbList(siteUrl, crumbs)]}
      />

      <Container size="lg" py="xl">
        <Stack gap="lg">
          <div>
            <Breadcrumb crumbs={crumbs} />
            <Group gap="xs" wrap="nowrap" align="center">
              <Title order={1}>{title}</Title>
              <FavoriteButton kind="tide" item={{ id: s.id, name: s.name }} />
            </Group>
          </div>

          {/* Server-rendered from the ingested slice, so it is in the HTML —
            unlike the panels below, which the browser fetches after hydration.
            Any active warnings become the head of this same card once that
            fetch lands; there is no separate banner. */}
          <ConditionsSummary
            conditions={marine_conditions}
            now={station_now}
            station={s}
            kind="tide"
            timezone={s.timezone}
            weather={weather}
          />

          <Grid gutter="lg">
            {/* Left: Now up top, then the week with each day's chart. */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="lg">
                <Stack gap="xs">
                  <TideNowCard
                    label={t('last_tide')}
                    tide={s.last_tide}
                    timezone={s.timezone}
                    timezoneAbbr={timezoneAbbr}
                    bg={TILE.bright}
                  />
                  <TideNowCard
                    label={t('next_tide')}
                    tide={s.next_tide}
                    timezone={s.timezone}
                    timezoneAbbr={timezoneAbbr}
                    bg={TILE.mid}
                  />
                  <AppPromo variant="horizontal" placement="tide-now" />
                  {s.next_solar && (
                    <SolarCard
                      solar={s.next_solar}
                      timezone={s.timezone}
                      timezoneAbbr={timezoneAbbr}
                      bg={TILE.dark}
                    />
                  )}
                  {s.next_lunar && (
                    <LunarCard
                      lunar={s.next_lunar}
                      timezone={s.timezone}
                      timezoneAbbr={timezoneAbbr}
                      bg={TILE.moon}
                    />
                  )}
                </Stack>

                <Stack gap="lg">
                  {days.map(([date, day], i) => {
                    // French (SHOM) stations only: collect the day's high-tide
                    // coefficients into one chip under the TIDES header, joined
                    // with " / " (SHOM annuaire style). Null for every other
                    // station, so no chip renders.
                    const dayCoef = (day.tidal_events || [])
                      .filter((e) => e.high && e.coefficient != null)
                      .map((e) => e.coefficient)
                      .join(' / ')
                    const dayBlock = (
                      <Stack gap="xs">
                        <Text ta="center" fw={700} fz="1.2rem">
                          {weekDate(t, locale, date, i)}
                        </Text>
                        <WeekCard>
                          <WeekSection
                            bg={TILE.bright}
                            emoji="🌊"
                            title={t('tides')}
                            badge={
                              dayCoef ? (
                                <CoefPill>{`${t('coefficient')} ${dayCoef}`}</CoefPill>
                              ) : null
                            }
                          >
                            {(day.tidal_events || []).map((e, j) => (
                              <WeekRow
                                key={j}
                                left={
                                  <ArrowLabel up={e.high} label={e.high ? t('high') : t('low')} />
                                }
                                time={fmt.time(e.timestamp, s.timezone)}
                                right={fmt.height(e.height_cm, e.height_ft)}
                              />
                            ))}
                            {/* Tide curve, inset inside the Tides band so it reads
                              as part of the tides block (not sun/moon). */}
                            <Box pt={12} pb={14} px={12}>
                              <TideChart day={day} globalY={s.global_y} timezone={s.timezone} />
                            </Box>
                          </WeekSection>
                          {/* The day's condition icon lives here in the header,
                            not in the band body — one icon per day. */}
                          {weatherDaily[i] ? (
                            <WeekSection
                              bg={TILE.mid}
                              emoji={dayConditionGlyph(weatherDaily[i])}
                              title={t('weather')}
                            >
                              <DayWeatherBand
                                day={weatherDaily[i]}
                                hours={weatherHourlyByDay[date] || []}
                                timezone={s.timezone}
                                date={date}
                                warnings={weatherWarnings}
                              />
                            </WeekSection>
                          ) : (
                            weather.status === 'loading' && <DayWeatherBandSkeleton />
                          )}
                          {day.solar_events && day.solar_events.length > 0 && (
                            <WeekSection bg={TILE.dark} emoji="☀️" title={t('sun')}>
                              {day.solar_events.map((e, j) => (
                                <WeekRow
                                  key={j}
                                  left={
                                    <ArrowLabel
                                      up={e.rise}
                                      label={e.rise ? t('sunrise_week') : t('sunset_week')}
                                    />
                                  }
                                  time={fmt.time(e.timestamp, s.timezone)}
                                />
                              ))}
                            </WeekSection>
                          )}
                          {day.lunar_events && day.lunar_events.length > 0 && (
                            <WeekSection
                              bg={TILE.moon}
                              emoji={moonPhaseEmoji(day.lunar_events[0].phase)}
                              title={t('moon')}
                            >
                              {day.lunar_events.map((e, j) => (
                                <WeekRow
                                  key={j}
                                  left={
                                    <ArrowLabel
                                      up={e.rise}
                                      label={e.rise ? t('moonrise_week') : t('moonset_week')}
                                    />
                                  }
                                  time={fmt.time(e.timestamp, s.timezone)}
                                  right={
                                    e.percentage != null ? `${Math.round(e.percentage * 100)}%` : ''
                                  }
                                />
                              ))}
                            </WeekSection>
                          )}
                        </WeekCard>
                      </Stack>
                    )
                    // One rectangle-height horizontal ad between today and
                    // tomorrow (only when there's a tomorrow to sit before).
                    if (i === 0 && days.length > 1) {
                      return (
                        <Fragment key={date}>
                          {dayBlock}
                          <AppPromo variant="horizontal" placement="tide-week" />
                        </Fragment>
                      )
                    }
                    return <Fragment key={date}>{dayBlock}</Fragment>
                  })}
                </Stack>
              </Stack>
            </Grid.Col>

            {/* Right: Map (square) up top, then weather + Insights (stub). */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="lg">
                {s.lat != null && s.lng != null && (
                  <StationMapPanel kind="tide" station={s} layerCoverage={layerCoverage} />
                )}
                <MarineForecastRail weather={weather} timezone={s.timezone} />
                <Insights
                  kind="tide"
                  insights={s.insights}
                  timezone={s.timezone}
                  midSlot={<AppPromo variant="rectangle" placement="tide-insights" />}
                />
                {/* Under the map, where the question "what chart is this water
                    on?" comes from. */}
                <ChartsCard charts={charts} stationName={s.name} />
              </Stack>
            </Grid.Col>
          </Grid>

          <Credits items={[s.attribution, ...(weather.data?.attribution || [])]} />
        </Stack>
      </Container>
    </>
  )
}

// App headline pattern: "{LABEL} - {HIGH|LOW}" uppercased; up=high, down=low.
// The web keeps the height as a stat line under the time.
function TideNowCard({ label, tide, timezone, timezoneAbbr, bg }) {
  const fmt = useFormat()
  const { t } = useI18n()
  const headline = tide ? `${label} - ${tide.high ? t('high') : t('low')}` : label
  // French (SHOM) high tides carry a tidal coefficient; a chip under the time.
  const coef = tide?.high && tide.coefficient != null ? tide.coefficient : null
  return (
    <NowTile
      bg={bg}
      headline={headline}
      arrowDeg={tide ? (tide.high ? 0 : 180) : null}
      afterArrow={
        tide && (
          <Text fz="1.7rem" fw={700} style={{ color: '#fff' }}>
            {fmt.height(tide.height_cm, tide.height_ft)}
          </Text>
        )
      }
      time={tide ? fmt.time(tide.timestamp, timezone) : null}
      iso={tide?.timestamp}
      tzAbbr={timezoneAbbr}
      beforeTime={coef != null && <CoefPill>{`${t('coefficient')} ${coef}`}</CoefPill>}
    />
  )
}

function SolarCard({ solar, timezone, timezoneAbbr, bg }) {
  const fmt = useFormat()
  const { t } = useI18n()
  const headline = solar ? (solar.rise ? t('sunrise_now') : t('sunset_now')) : t('sun')
  return (
    <NowTile
      bg={bg}
      glyph="☀️"
      headline={headline}
      time={solar ? fmt.time(solar.timestamp, timezone) : null}
      iso={solar?.timestamp}
      tzAbbr={timezoneAbbr}
    />
  )
}

function LunarCard({ lunar, timezone, timezoneAbbr, bg }) {
  const fmt = useFormat()
  const { t } = useI18n()
  const headline = lunar ? (lunar.rise ? t('moonrise_now') : t('moonset_now')) : t('moon')
  const phase =
    lunar && lunar.phase
      ? `${moonPhaseLabel(t, lunar.phase)}${
          lunar.percentage != null ? ` · ${Math.round(lunar.percentage * 100)}%` : ''
        }`
      : null
  return (
    <NowTile
      bg={bg}
      glyph={moonPhaseEmoji(lunar?.phase)}
      headline={headline}
      time={lunar ? fmt.time(lunar.timestamp, timezone) : null}
      iso={lunar?.timestamp}
      tzAbbr={timezoneAbbr}
      extra={phase && <TileStat>{phase}</TileStat>}
    />
  )
}
